/**
 * Campañas eliminadas en Meta (`DELETED`): el Front no las muestra (medida temporal; la API las
 * conserva a propósito y las devuelve en «Todas»). Ver docs/maps/meta-ads.md § Campañas.
 *
 * Además guarda, por espacio, cuántas eliminadas había en la última lista completa («Todas»)
 * para que «Todas (N)» no las cuente aunque la vista sea «Activas» (la API solo manda
 * `total_campaigns_count`, que sí las cuenta). Memoria del módulo (se pierde al recargar).
 * Sin imports a propósito: `setupTests` la limpia antes de cada test.
 */

const upper = (value) => String(value ?? '').trim().toUpperCase();

/**
 * `true` si la campaña está eliminada en Meta. Sirve para la campaña cruda de la API
 * (`status` / `effective_status` / `delivery_status`) y para la normalizada
 * (`effectiveStatus` / `delivery.status`). Archivadas (`ARCHIVED`) no cuentan.
 */
export function isDeletedMetaCampaign(campaign) {
  if (!campaign || typeof campaign !== 'object') return false;
  return [
    campaign.status,
    campaign.effective_status,
    campaign.effectiveStatus,
    campaign.delivery_status,
    campaign.delivery?.status,
  ].some((value) => upper(value) === 'DELETED');
}

/** espacio (`ownerUserId` o '' = el propio) → `{ rawTotal, ids: Set<string> }`. */
const cache = new Map();
const keyOf = (ownerUserId) => String(ownerUserId || '');

/**
 * Lo conocido para el espacio: `{ rawTotal, count }` o `null`. `rawTotal` es el
 * `total_campaigns_count` de la API cuando se contó (si cambia, el dato ya no vale).
 */
export function knownDeletedMetaCampaigns(ownerUserId) {
  const entry = cache.get(keyOf(ownerUserId));
  return entry ? { rawTotal: entry.rawTotal, count: entry.ids.size } : null;
}

/** Guarda las eliminadas de una lista completa (`rawTotal` = `total_campaigns_count`). */
export function rememberDeletedMetaCampaigns(ownerUserId, rawTotal, ids) {
  if (rawTotal == null) return;
  cache.set(keyOf(ownerUserId), { rawTotal, ids: new Set((ids || []).map(String)) });
}

/** Una campaña eliminada desde Terrio (la API la sigue contando en el total). */
export function rememberDeletedMetaCampaign(ownerUserId, campaignId) {
  const entry = cache.get(keyOf(ownerUserId));
  if (entry && campaignId != null) entry.ids.add(String(campaignId));
}

/** Tests. */
export function resetMetaDeletedCampaignsCache() {
  cache.clear();
}
